import tradeDataStore from "@/store/tradeDataStore";

/**
 * Safely parses a string rate to a number.
 * Returns undefined if the value is null, undefined, empty, or not a valid number.
 */
export function parseRate(rate: string | undefined): number | undefined {
  if (rate === undefined || rate === null || rate === "") return undefined;
  const parsed = parseFloat(rate);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Calculates the filled percentage based on fill quantity vs parent block quantity.
 *
 * @param fillQuantity - The quantity filled for this fill
 * @param blockQuantity - The total quantity of the parent block/leg
 * @returns The percentage filled (0-100)
 */
export function calculateFilledPercent(
  fillQuantity: number | undefined,
  blockQuantity: number | undefined,
  fillRate: number | undefined,
): number {
  // calculateFilledPercent updated to not include the % if there is no rate AND quantity.
  if (!fillQuantity || !blockQuantity || blockQuantity === 0 || !fillRate) {
    return 0;
  }

  return (fillQuantity / blockQuantity) * 100;
}

/**
 * Gets the parent block quantity from the Zustand store.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The parent block quantity or null
 */
export function getParentBlockQuantity(
  esid: string,
  blockId: number,
): number | null {
  const tradeData = tradeDataStore.getState().tradeData;
  const thisTrade = tradeData.find((t) => t.esid === esid);

  if (!thisTrade) {
    return null;
  }

  // For RP trades, check legs array
  if (thisTrade.role === "RP" && thisTrade.legs) {
    const leg = thisTrade.legs.find((l) => l.blockId === blockId);
    return leg?.notionalLeg1 ?? null;
  }

  // For OP trades, check executionRequestCounterParties > legs
  if (thisTrade.role === "OP" && thisTrade.executionRequestCounterParties) {
    for (const cp of thisTrade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg) {
          return leg.notionalLeg1 ?? null;
        }
      }
    }
  }

  return null;
}
